import React, { useState, useEffect, useReducer } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { KeyboardEventCheck } from '@Tools/KeyboardEventCheck';

// CSS
import styles from '@UILayer/UILayer.scss';

// MANAGERS
import { useAppSelector } from '@Managers/Redux/Hooks';
import { getButtonState, getGlobalStyles } from '@Managers/Redux/AppState/AppState';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EButtonEvents } from '@Managers/Events/ButtonEvents';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EBonusBuyEvents } from '@Managers/Events/BonusBuyEvents';

const BonusBuyButton: React.FC<{}> = ({}) => {
    const [state, setState] = useReducer((state: { active: boolean }, newState: any) => ({ ...state, ...newState }), { active: false });

    const globalStyles = useAppSelector(getGlobalStyles);
    const { t } = useTranslation();
    const gameStoreButtonState = useAppSelector(getButtonState);
    const buttonCN = cn(
        styles.bonus_buy_button,
        gameStoreButtonState?.isBonusBuyButtonActive ? styles.active : styles.disabled,
        globalStyles.orientation,
        globalStyles.wcaMode,
        globalStyles.handMode,
        globalStyles.wcaMode
    );

    useEffect(() => {}, []);

    function buttonAction(e: React.MouseEvent<any>) {
        if (gameStoreButtonState?.isBonusBuyButtonActive) EventsCenter.emit(EBonusBuyEvents.BONUS_BUY_DIALOG_OPEN);
    }

    return (
        <>
            <div className={buttonCN} onPointerDown={(e) => buttonAction(e)}>
                <svg viewBox="0 0 104 104">
                    <defs>
                        <linearGradient
                            id="linear-gradient"
                            x1="53.56"
                            y1="3185.99"
                            x2="50.32"
                            y2="3282.53"
                            gradientTransform="translate(0 -3180.5)"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop offset="0" stopColor="#e6ba08" />
                            <stop offset=".25" stopColor="#ce9e0a" />
                            <stop offset="1" stopColor="#8b4e10" />
                        </linearGradient>
                    </defs>
                    <g id="bg">
                        <circle className={styles.icon_background_fx} cx="52" cy="52" r="52" />
                        <circle className={styles.icon_background} cx="52" cy="52" r="52" />
                        <path
                            className={styles.icon_outline}
                            d="M52,2.5c27.3,0,49.5,22.2,49.5,49.5s-22.2,49.5-49.5,49.5S2.5,79.3,2.5,52,24.7,2.5,52,2.5M52,.5C23.6.5.5,23.6.5,52s23.1,51.5,51.5,51.5,51.5-23.1,51.5-51.5S80.4.5,52,.5h0Z"
                        />
                    </g>
                    <g id="icon">
                        <g id="text_x5F_group">
                            <path
                                className={styles.icon_text_fill_cutout}
                                d="M28.27,72.42h-2.32v3.04h1.95c.67,0,1.14-.08,1.42-.23s.42-.4.42-.74v-.91c0-.42-.11-.72-.32-.9-.22-.17-.6-.26-1.15-.26Z"
                            />
                            <path
                                className={styles.icon_text_fill_cutout}
                                d="M28.8,70.54c.21-.07.35-.18.43-.34.08-.16.11-.39.11-.69v-.56c0-.27-.04-.47-.11-.61-.07-.13-.21-.23-.43-.29-.22-.06-.56-.09-1.03-.09h-1.83v2.69h1.88c.43,0,.75-.04.97-.11Z"
                            />
                            <path
                                className={styles.icon_text_fill_cutout}
                                d="M41.55,68.12c-.43-.23-1.11-.34-2.02-.34s-1.59.11-2.02.33c-.43.22-.65.61-.65,1.16v5c0,.29.08.54.23.73.15.19.38.33.67.41.28.08.55.14.82.17.26.03.59.04.97.04s.71-.01.97-.04c.26-.03.54-.08.82-.17.28-.08.49-.22.64-.41s.22-.44.22-.73v-5c0-.54-.22-.92-.65-1.15Z"
                            />
                            <path
                                className={styles.icon_text}
                                d="M30.71,71.42v-.09c.34-.11.63-.3.89-.58.25-.28.38-.58.38-.9v-1.4c0-.62-.12-1.1-.37-1.45-.25-.34-.66-.6-1.25-.75-.59-.16-1.43-.24-2.52-.24h-4.62v11.39h4.74c1.54,0,2.69-.19,3.42-.58.74-.39,1.11-1.04,1.11-1.97v-1.75c0-.41-.18-.78-.55-1.12-.37-.33-.78-.52-1.22-.57ZM25.96,67.96h1.83c.47,0,.81.03,1.03.09.22.06.36.16.43.29.07.13.11.34.11.61v.56c0,.3-.04.54-.11.69-.08.16-.22.27-.43.34-.21.07-.53.11-.97.11h-1.88v-2.69ZM29.75,74.49c0,.34-.14.58-.42.74s-.75.23-1.42.23h-1.95v-3.04h2.32c.55,0,.93.09,1.15.26.22.18.32.47.32.9v.91Z"
                            />
                            <path
                                className={styles.icon_text}
                                d="M43.64,66.75c-.36-.23-.83-.43-1.39-.6-.49-.13-.94-.21-1.33-.25-.4-.04-.87-.06-1.4-.06-.46,0-.89.02-1.3.05-.45.04-.92.13-1.44.28-.52.15-.95.34-1.3.56-.4.26-.72.61-.97,1.05-.25.45-.38.95-.38,1.51v4.99c0,.68.18,1.26.53,1.73.35.47.84.82,1.46,1.03.57.2,1.13.34,1.66.42.53.08,1.11.12,1.75.12,1.31,0,2.49-.21,3.55-.63.59-.23,1.04-.58,1.38-1.04.33-.46.5-1,.5-1.63v-4.99c0-.61-.13-1.13-.39-1.56-.25-.42-.55-.75-.91-.98ZM42.2,74.28c0,.29-.07.54-.22.73s-.36.33-.64.41c-.28.08-.55.14-.82.17-.26.03-.59.04-.97.04s-.71-.01-.97-.04c-.26-.03-.54-.08-.82-.17-.29-.08-.52-.22-.67-.41-.15-.19-.23-.44-.23-.73v-5c0-.55.22-.94.65-1.16.43-.22,1.11-.33,2.02-.33s1.59.11,2.02.34c.43.23.65.61.65,1.15v5Z"
                            />
                            <polygon
                                className={styles.icon_text}
                                points="55.43 73.79 49.92 66.01 47.15 66.01 47.15 77.4 49.59 77.4 49.59 69.5 55.1 77.4 57.87 77.4 57.87 66.01 55.43 66.01 55.43 73.79"
                            />
                            <path
                                className={styles.icon_text}
                                d="M67.44,74.28c0,.52-.16.87-.48,1.06-.32.19-.89.29-1.69.29s-1.4-.1-1.73-.29-.5-.55-.5-1.06v-8.27h-2.74v8.27c0,2.2,1.64,3.3,4.93,3.3s4.95-1.1,4.95-3.3v-8.27h-2.74v8.27Z"
                            />
                            <path
                                className={styles.icon_text}
                                d="M80.47,71.21c-.59-.48-1.53-.72-2.83-.72h-1.37c-1.01,0-1.51-.3-1.51-.9v-.81c0-.35.17-.61.51-.77.32-.15.94-.23,1.86-.23.67,0,1.88.11,3.65.32v-2.04c-1.44-.15-2.63-.23-3.56-.23-1.8,0-3.12.23-3.95.7-.83.47-1.25,1.2-1.25,2.19v.84c0,1.99,1.25,2.98,3.76,2.98h1.33c.56,0,.95.1,1.17.3.22.2.32.49.32.86v.74c0,.78-.76,1.18-2.28,1.18-.73,0-2.08-.15-4.07-.46v2.09c1.6.21,2.95.32,4.04.32.8,0,1.48-.05,2.05-.15.57-.1,1.08-.27,1.55-.52.97-.48,1.46-1.4,1.46-2.76v-.75c0-.98-.29-1.71-.88-2.19Z"
                            />
                        </g>
                        <g id="icon_x5F_group">
                            <path
                                className={styles.icon}
                                d="M66.59,36.25c0,.11,0,.22-.03.33l-1.43,7.91c-.47,2.6-2.73,4.49-5.37,4.48h-16.13l-1.71-14.53-.43-3.63-.08-.64c-.32-2.74-2.65-4.81-5.41-4.81h-.4c-1,0-1.82.81-1.82,1.82s.81,1.82,1.82,1.82h.4c.92,0,1.7.69,1.8,1.6l2.5,21.25c.03.23.07.58.11.81l3.78-.05h15.56c4.39,0,8.16-3.15,8.94-7.47l1.43-7.91c.29-1.59-.14-3.22-1.18-4.46-.45-.54-1-.99-1.61-1.31l-.74,4.79Z"
                            />
                            <circle className={styles.icon} cx="46.12" cy="58.59" r="4.2" />
                            <circle className={styles.icon} cx="60.62" cy="58.59" r="4.2" />
                            <path
                                className={styles.icon}
                                d="M54.96,44.25l-1.88.2c-.44.05-.68-.15-.73-.59l-.26-2.41-.22.02c-1.59.17-2.87-.15-3.86-.96-.99-.81-1.57-2.02-1.74-3.63l-.06-.59c-.05-.44.15-.68.59-.73l2.66-.29c.44-.05.68.15.73.59l.02.22c.09.79.3,1.32.65,1.58.34.26.94.34,1.77.25l1.25-.14c.81-.09,1.37-.31,1.66-.66.29-.35.39-.95.29-1.81l-.05-.47c-.06-.52-.26-.91-.6-1.17-.34-.26-.73-.42-1.16-.48-.38-.04-1.13-.06-2.24-.04-1.11.02-1.94-.03-2.5-.12-.56-.1-1.14-.3-1.75-.62-1.25-.62-2-2.03-2.23-4.23l-.12-1.16c-.18-1.65.13-2.97.92-3.95.79-.98,1.99-1.56,3.6-1.74l.22-.02-.27-2.47c-.05-.44.15-.68.59-.73l1.88-.2c.44-.05.68.15.73.59l.27,2.47h.06c1.48-.17,2.7.16,3.66.99.96.83,1.52,2.03,1.69,3.62l.06.59c.05.44-.15.68-.59.73l-2.66.29c-.44.05-.68-.15-.73-.59l-.02-.22c-.09-.79-.3-1.32-.63-1.58-.33-.26-.93-.34-1.79-.25l-1,.11c-.86.09-1.42.32-1.69.67-.27.36-.35,1-.25,1.94l.07.63c.05.46.25.8.59,1.03.34.23.71.36,1.12.4.47.03,1.26.04,2.36.01,1.11-.03,1.95.02,2.52.14s1.15.34,1.73.65c1.19.67,1.9,2.03,2.12,4.08l.13,1.19c.18,1.63-.14,2.94-.95,3.94s-2,1.58-3.56,1.75h-.06s.26,2.42.26,2.42c.05.44-.15.68-.59.73Z"
                            />
                        </g>
                    </g>
                </svg>
            </div>
        </>
    );
};

export default BonusBuyButton;

/*
 onKeyDown={(e) => KeyboardEventCheck(e, buttonAction)}
            onPointerDown={(e) => buttonAction(e)}
*/
